// Must mirror TERMINAL_STATUSES in draft_generation_service.py. A 'degraded'
// draft is finished: it was produced through a fallback or non-AI path, but it
// is complete. Omitting it here would leave the UI polling forever on a draft
// that is actually done.
const TERMINAL_STATUSES = new Set(['ready', 'degraded', 'failed'])

export function isGenerationInProgress(status?: string | null): boolean {
  if (!status) return false
  return !TERMINAL_STATUSES.has(status)
}

// Codes written to assembly.generation_degradation by the backend. Each names
// a step that fell back, so the editor can tell which part of the draft to
// look at harder rather than being told only that something was off.
const DEGRADATION_REASONS: Record<string, string> = {
  naming_fallback_model: 'Section names came from the backup model.',
  naming_derived_labels: 'Section names were derived from clip descriptions, not written by the model.',
  clip_selection_score_sort: 'Clips were picked by score order, not chosen by the model.',
}

export function describeDegradations(degradation?: string | null): string[] {
  if (!degradation) return []
  return degradation
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)
    // An unrecognised code still gets shown. Saying a step fell back without
    // knowing which is more use than saying nothing.
    .map((code) => DEGRADATION_REASONS[code] ?? `A generation step fell back (${code}).`)
}

export function isDegraded(status?: string | null): boolean {
  return status === 'degraded'
}

export function generationPhaseLabel(status?: string | null): string {
  switch (status) {
    case 'pending':
      return 'Queued for generation'
    case 'generating':
      return 'Starting generation'
    case 'scoring':
      return 'Scoring moments'
    case 'clustering':
      return 'Grouping into sections'
    case 'naming':
      return 'Naming sections'
    case 'building':
      return 'Building draft structure'
    case 'finalizing':
      return 'Finalizing sequence'
    case 'ready':
      return 'Ready'
    case 'degraded':
      return 'Ready (reduced quality)'
    case 'failed':
      return 'Failed'
    default:
      return 'Generating draft'
  }
}
